import {priority} from '@priority-software/priority-proto-api/lib';
import {Dropdown, Text} from '@priority-software/priority-style-react';
import {observer} from 'mobx-react';
import React, {useEffect, useRef, useState} from 'react';

import {reportGeneratorsStore} from '../stores/ReportGeneratorsStore';
import styles from './styles.module.scss';
import {highlightText} from '../../../_legacy/library/utils/highlightText';
import texts from '../texts';
import {useBatchedFilteredList} from '../../../hooks/useBatchedFilteredList';
import {SearchDropdown} from '../../../components/commonOldtoNew';
import List from '../../../components/Common/List';

const getOptions = (forms: Map<string, priority.netitems.nrest.ITreeNode>) =>
    Array.from(forms, ([nodeId, form]) => ({value: nodeId, label: form.Title}));

interface Props {
    value: string;
    onSelect: (nodeId: string) => void;
    onClose?: () => void;
    direction?: 'rtl' | 'ltr';
    setSelectedFormName?: (formName: string) => void;
}
const FormsSelect = observer(
    ({value, onSelect, onClose, direction, setSelectedFormName}: Props) => {
        const {forms, currentReport} = reportGeneratorsStore.currentScreen;
        const [isOpen, setIsOpen] = useState(false);

        const [query, setQuery] = useState<string>();
        const optionsRef = useRef(getOptions(forms));
        const options = optionsRef?.current;

        const [selectedIndex, setselectedIndex] = useState<number>();

        const indexInList = options.findIndex(opt => opt.value === value);
        const batchSize = indexInList > 100 ? Math.ceil(indexInList / 100) * 100 : 100;

        const {batchedItems: filteredOptions, loadMore} = useBatchedFilteredList(
            options,
            batchSize,
            query ?? ''
        );

        const handleSelectItem = item => {
            setIsOpen(false);
            onSelect(item.value);
            setSelectedFormName && setSelectedFormName(item.label);
            setselectedIndex(filteredOptions.findIndex(opt => opt.value === item.value));
        };
        const title = forms.get(value)?.Title;

        useEffect(() => {
            handleClose();
        }, [currentReport]);

        const handleClose = () => {
            setIsOpen(false);
            setQuery(null);
            onClose?.();
        };

        useEffect(() => {
            setselectedIndex(filteredOptions.findIndex(opt => opt.value === value));
        }, [filteredOptions]);

        const handlePageDown = (index: number) => {
            const lastIndex = filteredOptions.length - 1;
            let nextIndex = Number(index) + 10;
            nextIndex = nextIndex > lastIndex ? lastIndex : nextIndex;
            return Promise.resolve({nextSelected: nextIndex});
        };

        const handlePageUp = selectedIndex => {
            let nextIndex = selectedIndex - 10;
            nextIndex = nextIndex >= 0 ? nextIndex : 0;
            return nextIndex;
        };

        const focsedIndex = query ? 0 : selectedIndex > -1 ? selectedIndex : 0;

        return (
            <SearchDropdown
                openOnFocus
                onOpen={() => setIsOpen(true)}
                focusedIndex={focsedIndex}
                clearOnClose={false}
                closeOnClear={false}
                closeOnBlur={false}
                shouldHandleHomeAndEndPress
                selectedIndex={selectedIndex}
                handlePageDown={handlePageDown}
                handlePageUp={handlePageUp}
                inputProps={{
                    value: query ?? title,
                    onChange: setQuery,
                    returnFocus: false,
                    displayClear: isOpen,
                    placeholder: texts.selectFormPlaceHolder,
                    autoSelect: true,
                    size: 'large',
                    componentClasses: styles.searchInput,
                    direction
                }}
                onClose={handleClose}
                circularNavigation
                withDataTestId
            >
                <Dropdown.Content componentClasses={styles.fieldsList}>
                    <Dropdown.Section gradient>
                        {filteredOptions?.length > 0 ? (
                            <List
                                onScrollToEnd={loadMore}
                                autoHeight
                                autoHeightMax={200}
                                data={filteredOptions}
                                renderItem={({item, index}) => (
                                    <Dropdown.Item
                                        divider={
                                            filteredOptions && index !== filteredOptions.length - 1
                                        }
                                        onClick={() => handleSelectItem(item)}
                                        key={index}
                                        index={index}
                                    >
                                        <Text skin="paragraph-2" textColor="primary-700">
                                            {highlightText(item.label, query, true, false, true)}
                                        </Text>
                                    </Dropdown.Item>
                                )}
                            />
                        ) : (
                            <Dropdown.Item disabled>
                                <Text componentClasses={styles.emptState} skin="paragraph-2">
                                    {texts.emptyStateFormSelect}
                                </Text>
                            </Dropdown.Item>
                        )}
                    </Dropdown.Section>
                </Dropdown.Content>
            </SearchDropdown>
        );
    }
);

export default FormsSelect;
